import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import classnames from "classnames";

import { useWebmasterSettings } from "context/admin/webmaster-settings";

import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import DetailsCard from "components/DetailsCard";
import DetailsCardList from "components/DetailsCardList";
import {
  PersonalDataModal,
  ChangePasswordModal,
  UserMobile,
  PasswordMobile,
  DeleteAccountMobile,
} from "page_components/shared/settings/Content/components";
import { DeleteAccountModal } from "page_components/admin/modals";
import ScrollTopMobile from "components/ScrollTopMobile";

import useResponsive from "libs/helpers/useResponsive";
import countries from "libs/countries.json";

import { ReactComponent as LightIcon } from "assets/icons/theme-light.svg";
import { ReactComponent as DarkIcon } from "assets/icons/theme-dark.svg";

const actions = [
  {
    type: "send_notification",
  },
  {
    type: "create_task",
  },
];

const mainClass = "webmasters-admin-settings";

const SettingsContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { max } = useResponsive();

  const {
    data,
    loading,
    error,
    photo_progress,
    updateSettings,
    updatePhoto,
    changePassword,
    deleteAccount,
  } = useWebmasterSettings();

  const [modal, setModal] = useState(null);
  const lastLanguage = useRef(null);
  const lastTheme = useRef(null);

  const changeLanguage = (language) => {
    if (language === (lastLanguage.current || data?.language)) return;
    lastLanguage.current = language;
    updateSettings(
      { language },
      language === "pl"
        ? t("User language changed to Polish.")
        : t("User language changed to English."),
    ).catch(() => {
      lastLanguage.current = null;
    });
  };

  const changeTheme = (theme) => {
    if (theme === (lastTheme.current || data?.theme)) return;
    lastTheme.current = theme;
    updateSettings(
      { theme },
      theme === "dark"
        ? t("User theme changed to dark.")
        : t("User theme changed to light."),
    ).catch(() => {
      lastTheme.current = null;
    });
  };

  const email = data?.email || "-";
  const phone = !!data?.phone?.number
    ? `${
        countries?.find((item) => item?.code === data?.phone?.code)
          ?.dialling_code || ""
      } ${data?.phone?.number}`
    : "-";

  const columns_data = [
    {
      title: t("User data"),
      action: {
        label: t("Edit"),
        onclick: () => setModal("personalData"),
      },
      items: [
        {
          type: "avatar",
          value: data?.photo?.location || data?.photo,
        },
        {
          label: `${t("Account type")}:`,
          value: t("Webmaster"),
        },
        {
          label: `${t("Full name")}:`,
          value: `${data?.first_name} ${data?.last_name}`,
        },
        {
          type: email !== "-" ? "link" : undefined,
          label: `${t("Email address")}:`,
          value:
            email !== "-"
              ? {
                  link_label: email,
                  link_href: `mailto:${email}`,
                }
              : email,
        },
        {
          type: phone !== "-" ? "link" : undefined,
          label: `${t("Phone number")}:`,
          value:
            phone !== "-"
              ? {
                  link_label: phone,
                  link_href: `tel:${phone.replace(/\s+/g, "")}`,
                }
              : phone,
        },
      ],
      hiddenMobile: true,
    },
    {
      title: t("Password"),
      action: {
        label: t("Change password"),
        onclick: () => setModal("changePassword"),
      },
      items: [
        {
          label: t("Last password change"),
          value: moment(data?.last_password_changed)?.fromNow(),
        },
        {
          type: "advice",
          value: t(
            "The password should be at least 10 characters long and include uppercase and lowercase letters, numbers, and special characters such as an exclamation mark. It should be unique for each website and changed periodically every few months. For convenience and increased security, we recommend using a password manager and two-factor authentication, for example through an app or security keys.",
          ),
        },
      ],
      hiddenMobile: true,
    },
    {
      type: "group",
      group: [
        {
          title: t("Panel language"),
          items: [
            {
              type: "radio",
              value: [
                {
                  icon: require("assets/icons/flags/pl.svg").default,
                  label: t("Polish"),
                  value: "pl",
                  selected: data?.language === "pl",
                  onChange: changeLanguage,
                },
                {
                  icon: require("assets/icons/flags/en.svg").default,
                  label: t("English"),
                  value: "en",
                  selected: data?.language === "en",
                  onChange: changeLanguage,
                },
              ],
            },
          ],
        },
        {
          title: t("Panel appearance"),
          items: [
            {
              type: "radio",
              value: [
                {
                  icon: <LightIcon />,
                  label: t("Light"),
                  value: "light",
                  selected: data?.theme === "light",
                  onChange: changeTheme,
                },
                {
                  icon: <DarkIcon />,
                  label: t("Dark"),
                  value: "dark",
                  selected: data?.theme === "dark",
                  onChange: changeTheme,
                },
              ],
            },
          ],
        },
        {
          title: t("Delete account"),
          items: [
            {
              type: "button",
              value: {
                button_label: t("Delete"),
                onclick: () => setModal("deleteAccount"),
              },
            },
          ],
          hiddenMobile: true,
        },
      ],
    },
  ];

  const columns_mobile_data = {
    user: {
      header: {
        title: t("User data"),
        action: {
          label: t("Edit"),
          onClick: () => setModal("personalData"),
        },
      },
      content: {
        avatar: data?.photo?.location,
        name: `${data?.first_name} ${data?.last_name} (Webmaster)`,
        email,
        phone,
      },
    },
    password: {
      header: {
        title: t("Password"),
      },
      content: {
        last_password_changed: data?.last_password_changed,
        advice: t(
          "The password should be at least 10 characters long and include uppercase and lowercase letters, numbers, and special characters such as an exclamation mark. It should be unique for each website and changed periodically every few months. For convenience and increased security, we recommend using a password manager and two-factor authentication, for example through an app or security keys.",
        ),
      },
      onClick: () => setModal("changePassword"),
    },
    delete_account: {
      onClick: () => setModal("deleteAccount"),
    },
  };

  useEffect(() => {
    if (data && data?.type !== "webmaster") {
      navigate("/webmasters", { replace: true });
    }
  }, [data, navigate]);

  return (
    <>
      <DashboardContentLayout
        backMobile={() => navigate(`/webmasters`)}
        header={{
          label: `${data?.first_name} ${data?.last_name}`,
          description: max.sm
            ? t("Webmasters")
            : {
                value: t("Webmasters"),
                onClick: () => navigate(`/webmasters`),
              },
          async: true,
        }}
        loading={loading || error}
        actions={actions}
        isAdaptive
      >
        <div className={mainClass}>
          {max.sm && (
            <>
              {!!columns_mobile_data?.user && (
                <div className={`${mainClass}__col`}>
                  <UserMobile {...columns_mobile_data.user} />
                </div>
              )}
              {!!columns_mobile_data?.password && (
                <div className={`${mainClass}__col`}>
                  <PasswordMobile {...columns_mobile_data.password} />
                </div>
              )}
            </>
          )}
          {columns_data.map((data, index) => {
            const isGrouped = data?.type === "group";

            if (max.sm && data?.hiddenMobile) return null;

            return (
              <div
                key={index}
                className={classnames(`${mainClass}__col`, {
                  [`${mainClass}__col--grouped`]: isGrouped,
                })}
              >
                {isGrouped ? (
                  data?.group?.map((item, index) => {
                    if (max.sm && item?.hiddenMobile) return null;

                    return (
                      <DetailsCard key={index} {...item}>
                        <DetailsCardList
                          items={item?.items}
                          view={item?.view}
                        />
                      </DetailsCard>
                    );
                  })
                ) : (
                  <DetailsCard {...data}>
                    <DetailsCardList items={data?.items} view={data?.view} />
                  </DetailsCard>
                )}
              </div>
            );
          })}
          {max.sm && (
            <>
              {!!columns_mobile_data?.delete_account && (
                <div className={`${mainClass}__col`}>
                  <DeleteAccountMobile
                    name={`${data?.first_name} ${data?.last_name}, Webmaster`}
                    {...columns_mobile_data.delete_account}
                  />
                </div>
              )}
              <ScrollTopMobile />
            </>
          )}
        </div>
      </DashboardContentLayout>

      <PersonalDataModal
        data={data}
        photo_progress={photo_progress}
        changeRole
        updateSettings={updateSettings}
        updatePhoto={updatePhoto}
        redirect="/webmasters"
        isOpen={modal === "personalData"}
        onClose={() => setModal(null)}
      />
      <ChangePasswordModal
        changePassword={changePassword}
        includeOldPassword={false}
        isOpen={modal === "changePassword"}
        onClose={() => setModal(null)}
      />
      <DeleteAccountModal
        title={`${data?.first_name} ${data?.last_name}, Webmaster`}
        isOpen={modal === "deleteAccount"}
        onSubmit={deleteAccount}
        onClose={() => setModal(null)}
      />
    </>
  );
};

export default SettingsContent;
